/**
 * Helper resize iframe partagé entre les modes `inline` et `popup`.
 *
 * Le widget envoie depuis l'iframe :
 *   parent.postMessage({ type: 'rtt-widget-resize', height: <px> }, '*')
 *
 * Côté SDK on filtre strictement sur (source iframe + origine baseUrl) pour
 * éviter qu'un autre iframe ou un script tiers ne triche.
 *
 * Ticket #704 : la hauteur demandée est plafonnée au viewport hôte et l'iframe
 * est ramenée dans le champ visible (scrollIntoView) pour que le footer du
 * BookingSheet reste accessible sur mobile.
 */

const LOG_PREFIX = '[RintintinBooking]';

/** Fraction max du viewport hôte pour la hauteur iframe (sheet + CTA visibles). */
export const WIDGET_IFRAME_MAX_VIEWPORT_RATIO = 0.92;

/**
 * Plafonne la hauteur demandée par l'iframe au viewport de la page hôte.
 * Sans viewport valide, retourne la hauteur demandée (arrondie) inchangée.
 */
export function capWidgetIframeHeight(
  requestedPx: number,
  hostViewportPx: number,
  maxRatio: number = WIDGET_IFRAME_MAX_VIEWPORT_RATIO
): number {
  if (!Number.isFinite(requestedPx) || requestedPx < 0) return 0;
  const rounded = Math.round(requestedPx);
  if (!Number.isFinite(hostViewportPx) || hostViewportPx <= 0) {
    return rounded;
  }
  const cap = Math.floor(hostViewportPx * maxRatio);
  return Math.max(0, Math.min(rounded, cap));
}

/**
 * Calcule l'origine attendue à partir d'une baseUrl normalisée.
 * Retourne `null` si l'URL est invalide (le caller passera alors en filtre permissif).
 */
export function resolveBaseOrigin(normalizedBaseUrl: string): string | null {
  try {
    return new URL(normalizedBaseUrl).origin;
  } catch {
    console.warn(`${LOG_PREFIX} baseUrl invalide :`, normalizedBaseUrl);
    return null;
  }
}

/**
 * Attache un listener postMessage qui ajuste la hauteur de l'iframe en fonction
 * des `rtt-widget-resize` reçus, et renvoie une fonction de cleanup.
 *
 * Filtre :
 *  - `event.source === iframe.contentWindow` (pas un autre iframe)
 *  - `event.origin === baseOrigin` (si fourni)
 *  - `event.data.type === 'rtt-widget-resize'`
 */
export function attachResizeListener(
  iframe: HTMLIFrameElement,
  baseOrigin: string | null
): () => void {
  const onMessage = (event: MessageEvent) => {
    if (event.source !== iframe.contentWindow) return;
    if (baseOrigin && event.origin !== baseOrigin) return;
    const data = event.data as { type?: string; height?: number } | null;
    if (!data || data.type !== 'rtt-widget-resize') return;
    const next = Number(data.height);
    if (!Number.isFinite(next)) return;
    const hostViewport =
      typeof window !== 'undefined' && Number.isFinite(window.innerHeight)
        ? window.innerHeight
        : 0;
    const capped = capWidgetIframeHeight(next, hostViewport);
    const prevHeight = parseInt(iframe.style.height || '0', 10) || 0;
    iframe.style.height = `${capped}px`;
    // Sheet ouvert → iframe grandit : ramener dans le viewport (mobile).
    if (capped > prevHeight + 40 && typeof iframe.scrollIntoView === 'function') {
      try {
        iframe.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } catch {
        /* scrollIntoView optionnel — certains hosts / old WebViews */
      }
    }
  };

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}
